import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ChevronRight } from "lucide-react";
import ImbalanceCard from "../components/ImbalanceCard";

export default function Progress() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [form, setForm] = useState({ body_weight: "", body_fat_pct: "", waist: "", chest: "", arm: "", thigh: "" });

    const refresh = async () => {
        const [m, s] = await Promise.all([api.get("/progress/measurements").then(r => r.data), api.get("/progress/stats").then(r => r.data)]);
        setItems(m);
        setStats(s);
    };
    useEffect(() => { refresh(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "").map(([k, v]) => [k, parseFloat(v)]));
        if (Object.keys(payload).length === 0) { toast.error("Enter at least one value"); return; }
        try {
            await api.post("/progress/measurements", payload);
            toast.success("Logged");
            setForm({ body_weight: "", body_fat_pct: "", waist: "", chest: "", arm: "", thigh: "" });
            refresh();
        } catch (e) {
            toast.error("Failed");
        }
    };

    const chartData = (stats?.weight_history || []).map(w => ({ date: new Date(w.date).toLocaleDateString(), weight: w.body_weight }));

    return (
        <div className="space-y-8" data-testid="progress-page">
            <div>
                <div className="label-eyebrow text-accent-red">Track everything that matters</div>
                <h1 className="font-display text-4xl font-black uppercase sm:text-5xl">Progress</h1>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                <div className="tactical-card p-6 lg:col-span-2">
                    <div className="label-eyebrow text-accent-red">Body weight trend</div>
                    <div className="mt-4 h-64 min-h-[240px]">
                        {chartData.length > 1 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid stroke="#2a2a2a" strokeDasharray="3 3" />
                                    <XAxis dataKey="date" stroke="#a1a1aa" fontSize={11} />
                                    <YAxis stroke="#a1a1aa" fontSize={11} domain={["auto", "auto"]} />
                                    <Tooltip contentStyle={{ background: "#141414", border: "1px solid #2a2a2a" }} />
                                    <Line type="monotone" dataKey="weight" stroke="#ff3b30" strokeWidth={2} dot={{ fill: "#ff3b30" }} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Log at least two weights to see a trend.</div>
                        )}
                    </div>
                </div>

                <form onSubmit={submit} className="tactical-card p-6 space-y-3" data-testid="measurement-form">
                    <div className="label-eyebrow text-accent-red">Log a measurement</div>
                    {["body_weight", "body_fat_pct", "waist", "chest", "arm", "thigh"].map(k => (
                        <div key={k}>
                            <label className="label-eyebrow text-muted-foreground">{k.replace("_", " ")}</label>
                            <input type="number" step="0.1" value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`meas-${k}`} className="mt-1 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm" />
                        </div>
                    ))}
                    <button type="submit" data-testid="meas-submit" className="w-full rounded-sm bg-accent-red px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)]">Log</button>
                </form>
            </div>

            {/* Side Imbalance Watch — same component as the dashboard; hides itself when no unilateral data exists. */}
            <ImbalanceCard />

            <div className="tactical-card p-6">
                <div className="flex items-center justify-between gap-3">
                    <div className="label-eyebrow text-accent-red">History · last 5</div>
                    <Link
                        to="/previous?tab=measurements"
                        data-testid="measurements-see-all"
                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground transition hover:text-accent-red"
                    >
                        See all <ChevronRight className="h-3 w-3" />
                    </Link>
                </div>
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full min-w-[600px] text-sm">
                        <thead>
                            <tr className="label-eyebrow text-muted-foreground">
                                <th className="py-2 text-left">Date</th>
                                <th className="py-2 text-left">Weight</th>
                                <th className="py-2 text-left">Body Fat %</th>
                                <th className="py-2 text-left">Waist</th>
                                <th className="py-2 text-left">Chest</th>
                                <th className="py-2 text-left">Arm</th>
                                <th className="py-2 text-left">Thigh</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && <tr><td colSpan={7} className="py-4 text-muted-foreground">No measurements yet.</td></tr>}
                            {items.slice().reverse().slice(0, 5).map(m => (
                                <tr key={m.id} className="border-t border-border">
                                    <td className="py-2">{new Date(m.date).toLocaleDateString()}</td>
                                    <td className="py-2">{m.body_weight ?? "—"}</td>
                                    <td className="py-2">{m.body_fat_pct ?? "—"}</td>
                                    <td className="py-2">{m.waist ?? "—"}</td>
                                    <td className="py-2">{m.chest ?? "—"}</td>
                                    <td className="py-2">{m.arm ?? "—"}</td>
                                    <td className="py-2">{m.thigh ?? "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
